import { GridCellParams, GridPinnedPosition } from '@mui/x-data-grid-pro';
import { AssessmentMethodType, FilterKeys } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DataGridTable,
  useCellStates,
  useColumnsWithCellState,
  useGridRefs,
  useNestedState,
} from '@schooly/components/data-grid';
import isEqual from 'lodash/isEqual';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { GridContainer } from '../../../components/uikit-components/Grid/Grid';
import { AssessmentMarkbookScrollArrows } from '../AssessmentMarkbookModal/AssessmentMarkbookScrollArrows';
import {
  createMarkbookArrangeByAssessmentForGroupsColumnGrouping,
  createMarkbookArrangeBySubjectForGroupsColumnGrouping,
} from './common/assessmentColumnGrouping';
import { createMarkbookForGroupsColumns } from './common/assessmentColumns';
import {
  getMarkbookForGroupsRows,
  useMarkbookForGroupsEntryUpdates,
} from './common/assessmentEntryUpdates';
import { useRenderMarkbookForGroupsEntryMethodWithMiddleware } from './common/assessmentMethodRendering';
import { middlewareConfig } from './common/assessmentMiddleware';
import { createMarkbookForGroupsPermissionChecker } from './common/assessmentPermissions';
import {
  ASSESSMENT_COL_WIDTH,
  ASSESSMENT_CSS_CLASSES,
  ASSESSMENT_GRID_SLOT_PROPS,
  BASELINE_COL_FIELD_KEY,
  getAssessmentColumnWidth,
  useMarkbookForGroupsStyling,
} from './common/assessmentStyling';
import { getAssessmentEntryColorGroupsBaselineFactory } from './common/colouredCells';
import { useGradeUpdateHandler } from './common/handlers/gradeHandlers';
import { useScoreUpdateHandler } from './common/handlers/scoreHandlers';
import { MarkbookCellContext } from './common/MarkbookCellContext';
import {
  AssessmentCellState,
  isMarkbookCategoryRow,
  isMarkbookForGroupsColumnData,
  MarkbookBaselineConfig,
  MarkbookCategoryWithStudents,
  MarkbookForGroupsProps,
  UpdateMarkbookForGroupsAssessmentEntryParams,
} from './common/types';
import { StickyRow, useTableStickyHeader } from './common/useTableStickyHeader';
import { getMarkbookSubjects } from './common/utils';

const COLUMN_BUFFER = 8;
export interface MarkbookForGroupsRef {
  updateEntries: (categories: MarkbookCategoryWithStudents[]) => void;
  apiRef: ReturnType<typeof useGridRefs>['apiRef'];
}

const createMarkbookForGroupsColumnGrouping = {
  [FilterKeys.Assessment]: createMarkbookArrangeByAssessmentForGroupsColumnGrouping,
  [FilterKeys.Subject]: createMarkbookArrangeBySubjectForGroupsColumnGrouping,
};

export const MarkbookForGroups = forwardRef<MarkbookForGroupsRef, MarkbookForGroupsProps>(
  (
    {
      assessments,
      categories: categoriesInit,
      lists = {},
      studentsHeaderCellProps,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
      onChangeSort,
      isSorting,
      sort,
      columnArrangeBy,
      showPercentage,
      getAssessmentEntryColor: propsGetAssessmentEntryColor,
      getAverageCellColor,
      onSuccessfulEntryUpdate,
      onApiRefReady,
      onUpdateError,
      onCellClickOverride,
      baseline,
      refetch,
      onSetBaselineConfig,
      showStudentsAverage,
    },
    ref,
  ) => {
    const { permissions } = useAuth();
    const { containerRef, apiRef } = useGridRefs();
    const cellContext = useCellStates<AssessmentCellState>();
    const styling = useMarkbookForGroupsStyling();
    const prevCategoriesInitRef = useRef(categoriesInit);

    useEffect(() => {
      onApiRefReady?.(apiRef);
    }, [apiRef, onApiRefReady]);

    const {
      state: categories = [],
      setState: setCategories,
      updateState,
    } = useNestedState(categoriesInit);
    useImperativeHandle(ref, () => ({ updateEntries: setCategories, apiRef }), [
      setCategories,
      apiRef,
    ]);
    const { updateEntry } = useMarkbookForGroupsEntryUpdates(updateState);
    const updateEntryWithMiddleware = useCallback(
      async (params: UpdateMarkbookForGroupsAssessmentEntryParams) => {
        updateEntry(params);
        refetch(params.categoryId);
        onSuccessfulEntryUpdate?.();
      },
      [onSuccessfulEntryUpdate, updateEntry, refetch],
    );

    const permissionChecker = useMemo(() => {
      if (onCellClickOverride) {
        return {
          isCellEditable: () => false,
        };
      }

      return createMarkbookForGroupsPermissionChecker(permissions);
    }, [permissions, onCellClickOverride]);

    const scoreUpdateHandler = useScoreUpdateHandler({
      cellContext,
      middlewareConfig: middlewareConfig[AssessmentMethodType.Score],
      updateLocalState: updateEntryWithMiddleware,
      onUpdateError,
    });

    const gradeUpdateHandler = useGradeUpdateHandler({
      cellContext,
      middlewareConfig: middlewareConfig[AssessmentMethodType.Grade],
      updateLocalState: updateEntryWithMiddleware,
      onUpdateError,
    });

    const renderAssessmentEntryMethodWithMiddleware =
      useRenderMarkbookForGroupsEntryMethodWithMiddleware({
        lists,
        onUpdateEntry: updateEntryWithMiddleware,
        cellContext,
        middlewareConfig,
      });

    const setBaselineConfig = useMemo(() => {
      if (!onSetBaselineConfig) return undefined;

      return (v: MarkbookBaselineConfig | null) => {
        onSetBaselineConfig?.(v);
        v
          ? apiRef?.current?.pinColumn(BASELINE_COL_FIELD_KEY, GridPinnedPosition.left)
          : apiRef?.current?.unpinColumn(BASELINE_COL_FIELD_KEY);
      };
    }, [onSetBaselineConfig, apiRef]);

    const rows = getMarkbookForGroupsRows({
      categories,
      hasNextPage,
      isFetchingNextPage,
    });
    const columns = useColumnsWithCellState(
      createMarkbookForGroupsColumns({
        assessments,
        showStudentsAverage,
        scoreUpdateHandler,
        permissionChecker,
        renderEntryCell: renderAssessmentEntryMethodWithMiddleware,
        styling,
        isFetchingNextPage,
        fetchNextPage,
        onChangeSort,
        sort,
        isSorting,
        gradeUpdateHandler,
        lists,
        onCellClickOverride,
        columnArrangeBy,
        baseline,
        onSetBaselineConfig: setBaselineConfig,
      }),
      cellContext.getCellState,
    );

    const stickyRowsData = useMemo(
      () =>
        rows.reduce<StickyRow[]>(
          (acc, row, idx) =>
            isMarkbookCategoryRow(row)
              ? [
                  ...acc,
                  {
                    ...row,
                    rowsCount: row.data.rowsCount,
                    rowIdx: idx,
                  },
                ]
              : acc,
          [],
        ),
      [rows],
    );

    const { stickyRows } = useTableStickyHeader({
      apiRef,
      rows: stickyRowsData,
    });

    const columnGroupingModel = createMarkbookForGroupsColumnGrouping[columnArrangeBy]({
      assessments,
      baseline,
      onColumnMouseOver: () => {},
      onMouseLeave: () => {},
      apiRef,
      rows,
      studentsHeaderCellProps,
      showStudentsAverage,
    });

    const canEdit = useCallback(
      ({ row, colDef }: GridCellParams) => {
        if (!isMarkbookForGroupsColumnData(colDef)) return false;

        return permissionChecker.isCellEditable({ row, column: colDef });
      },
      [permissionChecker],
    );

    const colWidths = useMemo(() => {
      switch (columnArrangeBy) {
        case FilterKeys.Assessment: {
          return [
            styling.studentNameCellWidth,
            ...assessments.map((a) => {
              const subjects = getMarkbookSubjects(a.groups);
              return subjects.length > 1
                ? subjects.reduce((acc, _) => acc + getAssessmentColumnWidth(a.methods.length), 0)
                : getAssessmentColumnWidth(a.methods.length);
            }),
          ];
        }
        case FilterKeys.Subject: {
          const subjects = getMarkbookSubjects(
            assessments.flatMap((assessment) => assessment.groups),
          );

          return [
            styling.studentNameCellWidth,
            ...(baseline ? [ASSESSMENT_COL_WIDTH] : []),
            ...subjects.flatMap((subject) => {
              const subjectGroupsIds = subject.groups.map(({ id }) => id);
              const subjectAssessments = assessments.filter((assessment) =>
                assessment.groups.some(({ id }) => subjectGroupsIds.includes(id)),
              );
              return [
                ...subjectAssessments.map((assessment) =>
                  getAssessmentColumnWidth(assessment.methods.length),
                ),
                ...(showStudentsAverage ? [ASSESSMENT_COL_WIDTH] : []),
              ];
            }),
          ];
        }
        default: {
          const _: never = columnArrangeBy;
          return _;
        }
      }
    }, [assessments, styling.studentNameCellWidth, columnArrangeBy, baseline, showStudentsAverage]);

    const isScrollable = useMemo(() => {
      const totalWidth = colWidths.reduce((acc, width) => acc + width, 0);
      const containerWidth = containerRef.current?.offsetWidth ?? 0;
      return totalWidth > containerWidth;
    }, [colWidths, containerRef]);

    useEffect(() => {
      if (!isEqual(prevCategoriesInitRef.current, categoriesInit)) {
        setCategories(categoriesInit);
        prevCategoriesInitRef.current = categoriesInit;
      }
    }, [categoriesInit, setCategories]);

    const columnsToPin = useMemo(() => {
      return baseline
        ? [...styling.dataGridProps.columnsToPin, BASELINE_COL_FIELD_KEY]
        : styling.dataGridProps.columnsToPin;
    }, [baseline, styling.dataGridProps.columnsToPin]);

    const getAssessmentEntryColor = useMemo(() => {
      if (!baseline) return propsGetAssessmentEntryColor;

      return getAssessmentEntryColorGroupsBaselineFactory(
        baseline,
        categories.flatMap(({ students }) => students),
        lists,
      );
    }, [baseline, categories, lists, propsGetAssessmentEntryColor]);

    return (
      <MarkbookCellContext.Provider
        value={{ showPercentage, getAssessmentEntryColor, getAverageCellColor }}
      >
        <GridContainer
          className={ASSESSMENT_CSS_CLASSES.gridContainer}
          ref={containerRef}
          sx={styling.gridContainerSx}
        >
          {isScrollable && (
            <AssessmentMarkbookScrollArrows
              apiRef={apiRef}
              containerRef={containerRef}
              pinnedColumns={baseline ? 2 : 1}
              colWidths={colWidths}
            />
          )}
          <DataGridTable
            rows={rows}
            columns={columns}
            isCellEditable={canEdit}
            apiRef={apiRef}
            columnGroupingModel={columnGroupingModel}
            pinnedRows={stickyRows}
            columnBuffer={COLUMN_BUFFER}
            {...styling.dataGridProps}
            columnsToPin={columnsToPin}
            sx={styling.dataGridSx}
            slotProps={ASSESSMENT_GRID_SLOT_PROPS}
          />
        </GridContainer>
      </MarkbookCellContext.Provider>
    );
  },
);
